import type { Dispensation } from "$global-providers/rxdb-provider/collections/dispensation";
import type { InvestigationActivity as RxInvestigationActivity } from "$global-providers/rxdb-provider/collections/investigation-activity";
import type { Prescription as RxPrescription } from "$global-providers/rxdb-provider/collections/prescription";
import type { Referral } from "$global-providers/rxdb-provider/collections/referral";
import type { InvestigationActivitiesProviderQuery } from "$routes/patient/[:patientId]/_lib/investigations/__generated__/investigation-activities-provider.gql";
import type { PatientHistoryFile } from "$routes/patient/[:patientId]/_lib/patient-history-file-upload";
import type { VaccinationConsultation } from "$routes/patient/[:patientId]/_lib/vaccination-history-card";
import type { HistoricAncEncounter } from "$routes/patient/[:patientId]/antenatal-care/_lib/anc-encounters";
import type { InvestigationActivityFragmentFragment } from "$routes/patient/[:patientId]/inpatient/investigations/_lib/__generated__/graphql.gql";
import type { EpocusEncounterWithExams } from "$routes/patient/[:patientId]/overview/history/_lib/use-patient-epocus-encounters";
import type { ImagingStudyWithImages } from "$routes/patient/[:patientId]/overview/history/_lib/use-patient-imaging-studies";
import type { Condition, Visit } from "elephant-web/gql";

export type { ImagingStudyWithImages };

export type HistoricEpocusEncounter = EpocusEncounterWithExams & {
  meta: EpocusEncounterWithExams["meta"] & { created: string };
};

/**
 * A historic investigation can either be an InvestigationActivity or the legacy TestResult type
 * This type exposes the union of them as they are defined in the query that fetches them
 */
type HistoricInvestigation =
  InvestigationActivitiesProviderQuery["investigationActivitiesPaginatedOffset"]["items"][0];

export type HistoricConsultation = {
  uuid?: string;
  diagnoses: readonly Condition[];
  diseases?: readonly { displayName: string; id: string }[];
  problem?: string;
  plan?: string;
  presentingComplaint?: string;
  meta: { created: string; createdByUserId: string };
  symptoms?: string[];
  location?: { name: string };
  deliveredBy?: { person?: { firstname?: string; lastname?: string } };
};

type RxInvestigationActivityWithLegacyCreated = RxInvestigationActivity & {
  meta: RxInvestigationActivity["meta"] & { created: string };
};

export type HistoricVisit = Omit<Visit, "careEvents">;

export type RxPrescriptionWithDispensationsAndLegacyCreated = RxPrescription & {
  meta: RxPrescription["meta"] & { created: string };
  dispensations: Array<Dispensation>;
};

export type HistoricReferral = Referral & {
  meta: Referral["meta"] & { created: string };
  allImagingStudies: ImagingStudyWithImages[];
  facilityName?: string;
};

export type HistoricPatientEvent =
  | HistoricConsultation
  | HistoricInvestigation
  | RxInvestigationActivityWithLegacyCreated
  | HistoricVisit
  | HistoricAncEncounter
  | RxPrescriptionWithDispensationsAndLegacyCreated
  | PatientHistoryFile
  | VaccinationConsultation
  | ImagingStudyWithImages
  | HistoricReferral
  | HistoricEpocusEncounter;

export function isVaccination(
  cardEntity: HistoricPatientEvent,
): cardEntity is VaccinationConsultation {
  return "isImmunisation" in cardEntity && cardEntity.isImmunisation === true;
}

export function isRxPrescription(
  cardEntity: HistoricPatientEvent,
): cardEntity is RxPrescriptionWithDispensationsAndLegacyCreated {
  return (
    "administrationApproach" in cardEntity && !("__typename" in cardEntity)
  );
}
export function isInvestigationActivity(
  cardEntity: HistoricPatientEvent,
): cardEntity is InvestigationActivityFragmentFragment {
  return (
    (cardEntity as InvestigationActivityFragmentFragment).__typename ===
    "InvestigationActivity"
  );
}

export function isRxInvestigationActivity(
  cardEntity: HistoricPatientEvent,
): cardEntity is RxInvestigationActivityWithLegacyCreated {
  return "catalogueItem" in cardEntity && !("__typename" in cardEntity);
}

export function isVisit(
  cardEntity: HistoricPatientEvent,
): cardEntity is HistoricVisit {
  return (cardEntity as HistoricVisit).__typename === "Visit";
}

export function isLegacyTestResult(
  cardEntity: HistoricPatientEvent,
): cardEntity is HistoricInvestigation {
  return (cardEntity as HistoricInvestigation).__typename === "TestResult";
}

export function isConsultation(
  cardEntity: HistoricPatientEvent,
): cardEntity is HistoricConsultation {
  return (
    ((cardEntity as HistoricConsultation).diagnoses?.length ?? 0) > 0 ||
    ((cardEntity as HistoricConsultation).diseases?.length ?? 0) > 0
  );
}

export function isAncEncounter(
  cardEntity: HistoricPatientEvent,
): cardEntity is HistoricAncEncounter {
  return (cardEntity as HistoricAncEncounter).__typename === "AncEncounter";
}

export function isImagingStudy(
  cardEntity: HistoricPatientEvent,
): cardEntity is ImagingStudyWithImages {
  return (
    "images" in cardEntity &&
    Array.isArray(cardEntity.images) &&
    "accessionNumber" in cardEntity &&
    !("__typename" in cardEntity)
  );
}

export function isReferral(
  cardEntity: HistoricPatientEvent,
): cardEntity is HistoricReferral {
  return (
    ("facilityId" in cardEntity || "freeTextFacility" in cardEntity) &&
    "reasons" in cardEntity &&
    "patientId" in cardEntity &&
    !("__typename" in cardEntity)
  );
}

export function isEpocusEncounter(
  cardEntity: HistoricPatientEvent,
): cardEntity is HistoricEpocusEncounter {
  return (
    "exams" in cardEntity &&
    Array.isArray((cardEntity as HistoricEpocusEncounter).exams)
  );
}
